/**
 * Keeps the horizontal filter bar on one line (collection + search pages).
 *
 * Horizon's <overflow-list> decides which filters fit and moves the rest into "More", but it
 * only re-measures when the bar itself resizes or filters are added/removed. Filters that get
 * wider afterwards — the web font finishing loading, or a filter becoming active (bold label,
 * "1" badge from cst-theme.css §17) — then wrap onto a second line instead of moving to More.
 *
 * This watches each filter's width and, when one changes, asks the list to re-measure. The
 * list's own public trigger is its `disabled` attribute: any change other than to "true"
 * reflows (assets/overflow-list.js → attributeChangedCallback). Stock Horizon files untouched.
 */

const SELECTOR = 'overflow-list.facets__overflow-list';

/** @type {WeakMap<Element, number>} */
const widths = new WeakMap();
/** @type {WeakSet<Element>} */
const watchedLists = new WeakSet();

/** @param {Element} list */
function reflow(list) {
  if (list.getAttribute('disabled') === 'true') return;
  list.setAttribute('disabled', 'false');
  list.removeAttribute('disabled');
}

/** @type {Set<Element>} */
const pending = new Set();
let scheduled = false;

/** @param {Element} list */
function scheduleReflow(list) {
  pending.add(list);
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    for (const l of pending) if (l.isConnected) reflow(l);
    pending.clear();
  });
}

// Each filter is an <accordion-custom> with display: contents (no box of its own), so watch
// its <summary>, the visible button whose width actually changes.
const itemObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const summary = entry.target;
    const item = summary.closest('overflow-list > *');
    // Items parked in the hidden "More" panel measure 0 wide; only visible items matter.
    if (!item || item.getAttribute('slot') === 'overflow') continue;

    const width = Math.round(entry.borderBoxSize?.[0]?.inlineSize ?? summary.getBoundingClientRect().width);
    if (width === 0) continue;

    const previous = widths.get(summary);
    widths.set(summary, width);
    const list = item.parentElement;
    if (previous !== undefined && previous !== width && list) scheduleReflow(list);
  }
});

/** @param {Element} list */
function observeItems(list) {
  for (const item of list.children) {
    const summary = item.querySelector('summary');
    if (summary) itemObserver.observe(summary);
  }
}

/** @param {Element} list */
function watch(list) {
  observeItems(list);
  if (watchedLists.has(list)) return;
  watchedLists.add(list);
  // Filters are re-rendered in place after each change; pick up any new items.
  new MutationObserver(() => observeItems(list)).observe(list, { childList: true, subtree: true });
}

function watchAll() {
  document.querySelectorAll(SELECTOR).forEach(watch);
}

let watchQueued = false;
watchAll();
// Facets are swapped in after filtering; look for new lists at most once per frame.
new MutationObserver(() => {
  if (watchQueued) return;
  watchQueued = true;
  requestAnimationFrame(() => {
    watchQueued = false;
    watchAll();
  });
}).observe(document.body, { childList: true, subtree: true });

// Web font swap widens every label at once: re-measure when fonts are ready.
document.fonts?.ready.then(() => document.querySelectorAll(SELECTOR).forEach((l) => scheduleReflow(l)));
